import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'next/router';
import Head from 'next/head';

import Paginator from '../components/02_molecules/Paginator';
import BBHeading from '../components/03_organisms/BodyBlocks/Text/BBHeading';
import BBRelatedContent from '../components/03_organisms/BodyBlocks/BBRelatedContent';
import BBBreadcrumbs from '../components/03_organisms/BodyBlocks/BBBreadcrumbs';
import TwoColumnsLayout from '../components/04_templates/ContentLayouts/TwoColumns';
import LandingPageTemplate from '../components/04_templates/PageLayouts/LandingPage';
import { scrollToElement } from '../utils/scroll-to';

import fetch from '../utils/fetch.js';
import { transformItems } from '../utils/transforms.dynamic_listing';

import '../styles/styles.scss';

const { parse } = require('url');

const debug = require('debug')('cw:pages:_dynamic-listing.js');

class DynamicListingPage extends React.Component {
  constructor(props) {
    super(props);

    this.onPageChange = this.onPageChange.bind(this);
  }

  static async getInitialProps({ query, res }) {
    const initialProps = {
      statusCode: 200,
      title: '',
      data: {},
      pager: {},
    };

    // Get route parts from the page query.
    const { type, value, page } = query;

    // See https://cw2.readme.io/docs/dynamic-listing-pages.
    const allowedPageTypes = ['type', 'country', 'topic', 'programme', 'author'];

    // Make sure the page type is correct.
    if (!allowedPageTypes.includes(type)) {
      debug('Page type %s is not allowed page type for dynamic listings page', type);
      if (res) res.status(404);
      initialProps.statusCode = 404;
      return initialProps;
    }

    try {
      const contentQuery = {
        // Amount ot items to display per page.
        items_per_page: 9,
        // Current page number. Taken either from page query or first page
        // by default.
        page: page && page > 0 ? page - 1 : 0,
      };

      // Content type is array in the query, all other arguments are usual strings.
      const argType = type === 'type' ? 'type[]' : type;
      contentQuery[argType] = value;

      // Fetch the whole page content using filters.
      const contentResponse = await fetch('/rest/list/content', {
        query: {
          ...contentQuery,
          _format: 'json',
        },
      });

      // If no data here it means that the filters are not valid. Throw 404.
      if (contentResponse.body.length === 0) {
        debug(
          'Dynamic page returned 404 because backend caught invalid filters data for query: %o',
          contentQuery,
        );
        if (res) res.status(404);
        initialProps.statusCode = 404;
        return initialProps;
      }

      const { results, pager } = contentResponse.body;

      // If no data at this point it simply means that the filter query has
      // no matching content on the backend. Theoretically we could have
      // still rendering the page and shown empty page placeholder.
      if (results.length === 0) {
        debug(
          'Dynamic page returned 404 because backend returned no data matching query: %o',
          contentQuery,
        );
        if (res) res.status(404);
        initialProps.statusCode = 404;
        return initialProps;
      }

      // Replaces slashes and underscores with space.
      const title = value.replace(/[_-]/g, ' ');

      // Custom titles for the specified node types.
      const titleReplacements = {
        news: 'News',
        story: 'Stories',
        publication: 'Publications',
        press_release: 'Press releases',
      };

      // For content type add the leading "s" chart to make it look like
      // "Press release" + "s".
      initialProps.title = type === 'type' ? titleReplacements[value] : title;

      // Prepare transformed data for <BBRelatedContent /> component.
      initialProps.data = {
        isMobileCollapsed: false,
        items: transformItems(results),
      };

      // Prepare transformed data for <Paginator /> component.
      if (!pager) {
        initialProps.pager = {
          currentPage: 1,
          totalPages: 1,
        };
      } else {
        initialProps.pager = {
          currentPage: pager.current_page + 1,
          totalPages: pager.pages,
        };
      }
    } catch (e) {
      debug(e);
      if (res) res.status(502);
      initialProps.statusCode = 502;
      return initialProps;
    }

    return initialProps;
  }

  /**
   * Handles pager click on the page.
   */
  async onPageChange(newPageNumber) {
    const { router } = this.props;

    // Insert a new page number into the query which will go into the
    // internal routing query.
    const hrefQuery = { ...router.query, page: newPageNumber };

    // Insert a new page number into the browser visible part of the query.
    const asPath = parse(router.asPath, true);
    const asPathQuery = { ...asPath.query, page: newPageNumber };

    // Full reload of the current page with new page argument.
    // It's intended to use Router instead or router here, because the
    // "router" prop passed from getInitialProps() loses all associated functions.
    router
      .push(
        { pathname: router.pathname, query: hrefQuery },
        { pathname: asPath.pathname, query: asPathQuery },
      )
      .then(() =>
        scrollToElement({
          elementId: 'dynamic-listing',
          offset: 20,
        }),
      );
  }

  render() {
    const { title, breadcrumbs, data, pager } = this.props;
    return (
      <LandingPageTemplate id="dynamic-listing">
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <TwoColumnsLayout
          main={[
            <BBBreadcrumbs breadcrumbs={{ links: breadcrumbs }} key="breadcrumbs" />,
            <BBHeading
              title={title}
              Tag="h1"
              style={{ textTransform: 'capitalize' }}
              key="heading"
            />,
          ]}
        />

        <BBRelatedContent {...data} />

        {pager && pager.totalPages > 1 && <Paginator {...pager} onPageChange={this.onPageChange} />}
      </LandingPageTemplate>
    );
  }
}

DynamicListingPage.propTypes = {
  router: PropTypes.shape(), // Next.js router.
  title: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      nextLink: PropTypes.shape({
        href: PropTypes.string,
        as: PropTypes.string,
      }),
    }),
  ),
  data: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string,
      alt: PropTypes.string,
    }),
    url: PropTypes.string,
    type: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    timestamp: PropTypes.number,
  }),
  pager: PropTypes.shape({
    totalPages: PropTypes.number.isRequired,
    currentPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func,
  }),
};

DynamicListingPage.defaultProps = {
  router: null,
  title: '',
  breadcrumbs: null,
  data: null,
  pager: null,
};

export default withRouter(DynamicListingPage);
